import { useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { ErrorResponse } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { Size } from '@ocean/types/dist/appearance'
import { layouts, common, controls, fields } from '@ocean/ui'
import { AnimatedSection, FinalState } from 'modules/Auth/components'
import { AuthFormLayout } from 'modules/Auth/layouts'
import { namespace } from 'modules/Auth/localization'
import authRoutes from 'modules/Auth/routing/routes'
import { RegisterParams } from 'modules/Auth/types'
import { Header, ErrorWrapper } from './RegistrationForm.styled'

const { Typography, Icon } = common
const { Button, common: commonControls } = controls
const { LoadingLayout } = layouts
const { InputField } = fields
const { ErrorMessage } = commonControls

const I18_PATH = `${namespace}:auth.registration`

type RegistrationFormProps = {
  onSubmit: (formData: RegisterParams) => void
  loading?: boolean
  error?: ErrorResponse
}

const RegistrationForm: React.FC<RegistrationFormProps> = props => {
  const { onSubmit, loading, error } = props
  const prevLoading = usePrevious(loading)
  const [formikBag, setFormikBag] = useState<FormikHelpers<RegisterParams> | null>(null)
  const [requestSuccessful, setSuccessState] = useState(false)

  useEffect(() => {
    if (prevLoading && !loading && formikBag) {
      formikBag.setSubmitting(false)

      if (!error) {
        setSuccessState(true)
      }
    }
  }, [formikBag, loading, prevLoading, error])

  const { t } = useTranslation()

  const VALIDATION_SCHEMA: yup.SchemaOf<RegisterParams> = yup.object().shape({
    installationName: yup.string().required(t(`${I18_PATH}.installationNameRequired`)),
    customer: yup.string().required(t(`${I18_PATH}.companyNameRequired`)),
    serialNumber: yup.string().required(t(`${I18_PATH}.serialNumberRequired`)),
  })

  const INITIAL_VALUES: RegisterParams = {
    installationName: '',
    customer: '',
    serialNumber: '',
  }

  function handleSubmitForm(values: RegisterParams, actions: FormikHelpers<RegisterParams>) {
    onSubmit(values)
    setFormikBag(actions)
  }

  function renderSuccess() {
    return (
      <FinalState
        title={t(`${I18_PATH}.registrationCompleted`)}
        description={t(`${I18_PATH}.completedDescription`)}
        backText={t(`${I18_PATH}.login`)}
        backPath={authRoutes.login}
        dataTest='RegistrationFormSuccess'
      />
    )
  }

  function renderForm() {
    return (
      <>
        <AnimatedSection animationDuration='0.6s'>
          <Header data-test='RegistrationFormHeader'>
            <Typography variant='h3'>{t(`${I18_PATH}.registerInstallation`)}</Typography>
          </Header>
        </AnimatedSection>
        {error && (
          <ErrorWrapper>
            <ErrorMessage error={error.error} dataTest='RegistrationFormErrorMessage' />
          </ErrorWrapper>
        )}
        <Formik
          validationSchema={VALIDATION_SCHEMA}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmitForm}
          validateOnMount
          validateOnBlur
          validateOnChange
        >
          {({ isValid, isSubmitting }) => (
            <LoadingLayout isLoading={loading}>
              <Form>
                <AuthFormLayout>
                  <AnimatedSection animationDuration='0.7s'>
                    <Field
                      title={t(`${I18_PATH}.installationName`)}
                      name='installationName'
                      icon={<Icon icon='company' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.8s'>
                    <Field
                      title={t(`${I18_PATH}.companyName`)}
                      name='customer'
                      icon={<Icon icon='user' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.9s'>
                    <Field
                      title={t(`${I18_PATH}.serialNumber`)}
                      name='serialNumber'
                      icon={<Icon icon='lock' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='1.0s'>
                    <Button
                      fill
                      type='submit'
                      title={t(`${I18_PATH}.register`)}
                      disabled={!isValid || isSubmitting}
                      dataTest='RegistrationFormSubmitButton'
                    />
                  </AnimatedSection>
                </AuthFormLayout>
              </Form>
            </LoadingLayout>
          )}
        </Formik>
      </>
    )
  }

  return !requestSuccessful ? renderForm() : renderSuccess()
}

export default memo(RegistrationForm)
